import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import VideoModal from "@/components/shared/videoModal";
import { getThumbnailUrl } from "@/lib/videoHelpers";
import { Play } from "lucide-react";

interface MediaItem {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  display_order: number;
}

interface MediaGalleryWidgetProps {
  projectId: string;
}

export default function MediaGalleryWidget({
  projectId,
}: MediaGalleryWidgetProps) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<MediaItem | null>(null);

  useEffect(() => {
    fetchMediaItems();
  }, [projectId]);

  const fetchMediaItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("project_media")
        .select("*")
        .eq("project_id", projectId)
        .order("display_order", { ascending: true });

      if (fetchError) throw fetchError;

      setMediaItems(data || []);
    } catch (err) {
      console.error("Error fetching media items:", err);
      setError("Failed to load media gallery");
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no media items exist
  if (!loading && mediaItems.length === 0) {
    return null;
  }

  // Don't render if there's an error
  if (error) {
    return null;
  }

  const handleCardClick = (item: MediaItem) => {
    setSelectedVideo(item);
  };

  const handleCloseModal = () => {
    setSelectedVideo(null);
  };

  return (
    <>
      <Card variant="bordered">
        <CardHeader color="blue">
          <h2 className="text-lg sm:text-xl font-bold text-black">
            Media Gallery
          </h2>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">Loading media...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mediaItems.map((item) => {
                const thumbnail = getThumbnailUrl(
                  item.video_url,
                  item.thumbnail_url || undefined
                );

                return (
                  <div
                    key={item.id}
                    onClick={() => handleCardClick(item)}
                    className="group cursor-pointer border-2 border-black bg-white hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all duration-300"
                  >
                    {/* Thumbnail */}
                    <div className="relative bg-gray-900 aspect-video overflow-hidden">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-600" />
                      )}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                        <div className="bg-white rounded-full p-3 sm:p-4 group-hover:scale-110 transition-transform">
                          <Play className="w-6 h-6 sm:w-8 sm:h-8 fill-black stroke-black" />
                        </div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-3 sm:p-4">
                      <h3 className="font-bold text-sm sm:text-base text-black mb-1 line-clamp-2">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal
          isOpen={!!selectedVideo}
          onClose={handleCloseModal}
          videoUrl={selectedVideo.video_url}
          title={selectedVideo.title}
          description={selectedVideo.description || undefined}
        />
      )}
    </>
  );
}
